import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  MapPin,
  Briefcase,
  Building2,
  ArrowUpRight,
  Filter,
  X,
  Sparkles,
  Check,
  BellRing,
} from "lucide-react";
import { listJobs, jobFacets } from "@/lib/api";
import { useCandidate } from "@/lib/candidate";
import { rankJobs, matchBadge } from "@/lib/jobMatch";
import { JOBS, SAVED } from "@/constants/testIds";
import SaveSearchModal from "@/components/SaveSearchModal";
import FaqBlock from "@/components/FaqBlock";
import Seo from "@/components/Seo";
import { JOBS_FAQS } from "@/data/faqs";
import { jobItemListSchema, faqSchema, breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

const formatSalary = (min, max) => {
  const fmt = (n) => {
    if (n == null) return "";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1).replace(/\.0$/, "")} Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(0)} L`;
    return `₹${n}`;
  };
  if (min && max) return `${fmt(min)} — ${fmt(max)}`;
  if (min) return `${fmt(min)}+`;
  if (max) return `Up to ${fmt(max)}`;
  return "Competitive";
};

const daysAgo = (iso) => {
  try {
    const d = new Date(iso);
    const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days}d ago`;
  } catch {
    return "";
  }
};

const useMultiParam = (params, key) => useMemo(() => params.getAll(key), [params, key]);

export default function JobsPage() {
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const { parsedCV, clear } = useCandidate();
  const navigate = useNavigate();

  const industries = useMultiParam(params, "industry");
  const techs = useMultiParam(params, "tech");
  const seniorities = useMultiParam(params, "seniority");
  const remotes = useMultiParam(params, "remote");
  const search = params.get("search") || "";

  const candidateSkills = parsedCV?.skills || [];
  const hasCandidate = candidateSkills.length > 0;

  const rankedJobs = useMemo(
    () => rankJobs(jobs, candidateSkills),
    [jobs, candidateSkills]
  );
  const strongMatches = useMemo(
    () => rankedJobs.filter((j) => (j._match?.score ?? 0) >= 70).length,
    [rankedJobs]
  );

  const toggleArrayParam = (key, value) => {
    const next = new URLSearchParams(params);
    const values = next.getAll(key);
    next.delete(key);
    if (values.includes(value)) {
      values.filter((v) => v !== value).forEach((v) => next.append(key, v));
    } else {
      values.forEach((v) => next.append(key, v));
      next.append(key, value);
    }
    setParams(next, { replace: true });
  };

  const setSearch = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("search", v);
    else next.delete("search");
    setParams(next, { replace: true });
  };

  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  useEffect(() => {
    jobFacets().then(setFacets).catch(() => setFacets(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (industries.length) query.industry = industries;
    if (techs.length) query.tech = techs;
    if (seniorities.length) query.seniority = seniorities;
    if (remotes.length) query.remote = remotes;
    if (search) query.search = search;
    listJobs(query)
      .then(setJobs)
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [industries, techs, seniorities, remotes, search]);

  const anyActive =
    industries.length || techs.length || seniorities.length || remotes.length || search;

  return (
    <div className="bg-[var(--bg)] min-h-screen" data-testid={JOBS.page}>
      <Seo
        title="Jobs — Recruitment across SaaS/IT, Retail & Banking"
        description="Live openings across SaaS/IT, Retail, Banking and Real Estate. Filter by tech stack, seniority and remote type. Drop your CV once — Orphion matches you to every role that fits."
        path="/jobs"
        jsonLd={[
          jobItemListSchema(jobs),
          faqSchema(JOBS_FAQS),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Jobs", path: "/jobs" },
          ]),
        ]}
      />
      {/* Header */}
      <section className="pt-40 pb-12 md:pb-16 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="overline mb-6">Careers · Live openings</p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.92] text-[var(--ink)]"
            >
              Roles we&apos;re{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                hiring for
              </span>
              .
            </motion.h1>
          </div>
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              className="text-[var(--ink-80)] text-lg leading-relaxed max-w-2xl"
            >
              Curated openings with GCCs and AI-native IT companies. Filter by
              tech stack and stage. Drop your CV once — our AI matches you to
              every role that fits.
            </motion.p>

            {/* Search */}
            <div className="mt-8 relative max-w-2xl">
              <Search className="w-4 h-4 text-[var(--ink-50)] absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                placeholder="Search by role, company or keyword…"
                className="editorial-input editorial-input-icon"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid={JOBS.search}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Candidate personalization banner */}
      {hasCandidate && (
        <motion.section
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="border-b border-[var(--line-strong)] bg-[var(--bg-2)]"
          data-testid="jobs-candidate-banner"
        >
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start md:items-center gap-3 min-w-0">
              <Sparkles className="w-5 h-5 text-[var(--accent)] shrink-0 mt-1 md:mt-0" strokeWidth={1.7} />
              <p className="text-sm text-[var(--ink)]">
                Personalised for{" "}
                <span className="font-medium">
                  {parsedCV.name || "your CV"}
                </span>{" "}
                — <span className="font-serif-accent italic text-[var(--accent)]">{strongMatches}</span>{" "}
                strong match{strongMatches === 1 ? "" : "es"} across{" "}
                <span className="font-medium">{rankedJobs.length}</span> role
                {rankedJobs.length === 1 ? "" : "s"}. Sorted by fit.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => navigate("/apply")}
                className="text-xs uppercase tracking-[0.22em] text-[var(--ink)] link-slide"
                data-testid="jobs-edit-profile"
              >
                Edit profile
              </button>
              <button
                onClick={clear}
                className="text-xs uppercase tracking-[0.22em] text-[var(--ink-70)] hover:text-[var(--accent)]"
                data-testid="jobs-clear-candidate"
              >
                Clear CV
              </button>
            </div>
          </div>
        </motion.section>
      )}

      {/* Main grid */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid md:grid-cols-12 gap-10">
          {/* Filters */}
          <aside
            className={`md:col-span-3 md:sticky md:top-24 md:self-start ${
              mobileFiltersOpen ? "block" : "hidden md:block"
            }`}
          >
            <div className="flex items-center justify-between mb-6 gap-2">
              <p className="overline">Filters</p>
              {anyActive ? (
                <button
                  onClick={clearAll}
                  className="text-xs uppercase tracking-[0.22em] text-[var(--accent)] link-slide"
                  data-testid={JOBS.clear}
                >
                  Clear
                </button>
              ) : null}
            </div>

            <button
              onClick={() => setSaveOpen(true)}
              className="w-full mb-8 border border-[var(--line-strong)] hover:border-[var(--accent)] hover:bg-[var(--bg-2)] transition-colors p-4 text-left group"
              data-testid={SAVED.openBtn}
            >
              <div className="flex items-start gap-3">
                <BellRing
                  className="w-5 h-5 text-[var(--accent)] mt-0.5 shrink-0 group-hover:scale-110 transition-transform"
                  strokeWidth={1.7}
                />
                <div className="min-w-0">
                  <p className="font-display text-base text-[var(--ink)] leading-tight">
                    Save this search
                  </p>
                  <p className="text-xs text-[var(--ink-70)] mt-1 leading-relaxed">
                    Get an email the{" "}
                    <span className="text-[var(--accent)] font-medium">moment</span>{" "}
                    a matching role opens.
                  </p>
                </div>
              </div>
            </button>

            <FilterGroup
              title="Industry"
              options={facets?.industries || []}
              selected={industries}
              onToggle={(v) => toggleArrayParam("industry", v)}
              testIdFn={JOBS.filterIndustry}
            />
            <FilterGroup
              title="Tech Stack"
              options={(facets?.tech_stack || []).map((t) => t.name)}
              counts={Object.fromEntries((facets?.tech_stack || []).map((t) => [t.name, t.count]))}
              selected={techs}
              onToggle={(v) => toggleArrayParam("tech", v)}
              testIdFn={JOBS.filterTech}
              scrollable
            />
            <FilterGroup
              title="Seniority"
              options={facets?.seniorities || []}
              selected={seniorities}
              onToggle={(v) => toggleArrayParam("seniority", v)}
              testIdFn={JOBS.filterSeniority}
            />
            <FilterGroup
              title="Remote"
              options={facets?.remote_types || []}
              selected={remotes}
              onToggle={(v) => toggleArrayParam("remote", v)}
              testIdFn={JOBS.filterRemote}
            />
          </aside>

          {/* Results */}
          <div className="md:col-span-9">
            <div className="md:hidden mb-6">
              <button
                onClick={() => setMobileFiltersOpen((s) => !s)}
                className="btn-ghost w-full justify-between"
              >
                <span className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                  {anyActive ? (
                    <span className="w-2 h-2 rounded-full bg-[var(--accent)]" />
                  ) : null}
                </span>
                <X className={`w-4 h-4 transition-transform ${mobileFiltersOpen ? "" : "rotate-45"}`} />
              </button>
            </div>

            <p
              className="text-sm uppercase tracking-[0.22em] text-[var(--ink-50)] mb-6"
              data-testid={JOBS.count}
            >
              {loading
                ? "Loading…"
                : `${rankedJobs.length} open ${rankedJobs.length === 1 ? "role" : "roles"}${
                    hasCandidate ? " · sorted by match" : ""
                  }`}
            </p>

            {!loading && rankedJobs.length === 0 && (
              <div
                data-testid={JOBS.empty}
                className="border border-[var(--line-strong)] bg-[var(--bg-2)] p-12 text-center"
              >
                <p className="font-serif-accent italic text-[var(--accent)] text-2xl mb-2">
                  Nothing matches — yet.
                </p>
                <p className="text-[var(--ink-70)]">
                  Try a broader filter set, or drop your CV and we&apos;ll match
                  you to future roles.
                </p>
              </div>
            )}

            <div className="space-y-4">
              {rankedJobs.map((job, i) => (
                <JobCard
                  key={job.id}
                  job={job}
                  index={i}
                  activeTechs={techs}
                  onToggleTech={(t) => toggleArrayParam("tech", t)}
                  hasCandidate={hasCandidate}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <SaveSearchModal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        filters={{ industry: industries, tech: techs, seniority: seniorities, remote: remotes, search }}
      />

      <FaqBlock
        kicker="Jobs · FAQ"
        title="What to expect when you apply."
        items={JOBS_FAQS}
      />
    </div>
  );
}

const JobCard = ({ job, index, activeTechs, onToggleTech, hasCandidate }) => {
  const match = job._match;
  const badge = match ? matchBadge(match.score) : null;
  const toneClass =
    badge?.tone === "strong"
      ? "bg-[var(--accent)] text-white border-[var(--accent)]"
      : badge?.tone === "good"
      ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
      : badge?.tone === "partial"
      ? "bg-[var(--bg)] text-[var(--ink)] border-[var(--line-strong)]"
      : "bg-transparent text-[var(--ink-70)] border-[var(--line)]";
  const matched = new Set((match?.matched || []).map((s) => s.toLowerCase()));

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.03 * index, ease: EASE }}
      data-testid={JOBS.card(job.id)}
      className="border border-[var(--line-strong)] bg-[var(--bg)] hover:bg-[var(--bg-2)] transition-colors p-6 md:p-8"
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-1">
              {job.industry}
            </span>
            {hasCandidate && badge && (
              <span
                data-testid={`jobs-match-${job.id}`}
                className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border inline-flex items-center gap-1.5 ${toneClass}`}
                title={`${match.matched.length} of ${(match.matched.length + match.missing.length) || 0} skills match`}
              >
                <Sparkles className="w-3 h-3" strokeWidth={2} />
                {match.score}% · {badge.label}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)]">
              {daysAgo(job.posted_at)}
            </span>
          </div>
          <h3 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[var(--ink)] leading-[1.1]">
            <Link to={`/jobs/${job.id}`} className="link-slide">
              {job.title}
            </Link>
          </h3>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[var(--ink-80)]">
            <span className="inline-flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-[var(--ink-50)]" />
              {job.company}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[var(--ink-50)]" />
              {job.location} · {job.remote_type}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5 text-[var(--ink-50)]" />
              {job.seniority}
            </span>
          </div>
          <p className="mt-4 text-[var(--ink-70)] text-sm leading-relaxed max-w-2xl">
            {job.description}
          </p>
          {job.tech_stack?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {job.tech_stack.map((t) => {
                const isMatched = matched.has(t.toLowerCase());
                const isActive = activeTechs.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => onToggleTech(t)}
                    className={`text-xs px-2 py-0.5 rounded-full border transition-colors inline-flex items-center gap-1 ${
                      isActive
                        ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                        : isMatched
                        ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5"
                        : "border-[var(--line-strong)] text-[var(--ink)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                    }`}
                  >
                    {isMatched && <Check className="w-3 h-3" strokeWidth={2.5} />}
                    {t}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="md:text-right space-y-3 shrink-0">
          <p className="font-display text-lg text-[var(--ink)]">
            {formatSalary(job.salary_min, job.salary_max)}
          </p>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)]">
            {job.openings} opening{job.openings === 1 ? "" : "s"}
          </p>
          <Link
            to={`/apply?job=${job.id}`}
            className="btn-pill text-sm"
            data-testid={JOBS.applyBtn(job.id)}
          >
            <span>Apply</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

const FilterGroup = ({
  title,
  options,
  selected,
  onToggle,
  testIdFn,
  counts,
  scrollable,
}) => (
  <div className="mb-8">
    <p className="text-[10px] uppercase tracking-[0.24em] text-[var(--ink-50)] mb-3">
      {title}
    </p>
    <div className={`space-y-2 ${scrollable ? "max-h-64 overflow-y-auto pr-2" : ""}`}>
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <label
            key={opt}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <input
              type="checkbox"
              checked={active}
              onChange={() => onToggle(opt)}
              className="sharp appearance-none w-4 h-4 border border-[var(--line-strong)] checked:bg-[var(--accent)] checked:border-[var(--accent)] transition-colors"
              data-testid={testIdFn ? testIdFn(opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()) : undefined}
            />
            <span
              className={`text-sm ${
                active ? "text-[var(--ink)]" : "text-[var(--ink-70)]"
              } group-hover:text-[var(--ink)]`}
            >
              {opt}
              {counts?.[opt] != null && (
                <span className="text-[var(--ink-50)] ml-1">({counts[opt]})</span>
              )}
            </span>
          </label>
        );
      })}
      {options.length === 0 && (
        <p className="text-xs text-[var(--ink-50)]">
          {scrollable ? "None yet" : "—"}
        </p>
      )}
    </div>
  </div>
);
